export interface SyncWork {
  directory: boolean;
  chatId: string | null;
}

/** Serialize invalidations and retain hints received while a request is in flight. */
export class SyncQueue {
  private pending: SyncWork = { directory: false, chatId: null };
  private timer?: ReturnType<typeof setTimeout>;
  private running = false;
  private stopped = false;
  private readonly run: (work: SyncWork) => Promise<void>;
  private readonly failed: (error: unknown, work: SyncWork) => void;
  private readonly idle: () => void;

  constructor(
    run: (work: SyncWork) => Promise<void>,
    failed: (error: unknown, work: SyncWork) => void,
    idle: () => void,
  ) {
    this.run = run;
    this.failed = failed;
    this.idle = idle;
  }

  request(work: Partial<SyncWork>) {
    if (this.stopped) return;
    this.pending.directory ||= Boolean(work.directory);
    if (work.chatId !== undefined) this.pending.chatId = work.chatId;
    if (!this.running && !this.timer)
      this.timer = setTimeout(() => void this.drain(), 40);
  }

  stop() {
    this.stopped = true;
    clearTimeout(this.timer);
    this.pending = { directory: false, chatId: null };
  }

  private async drain() {
    this.timer = undefined;
    this.running = true;
    while (!this.stopped && (this.pending.directory || this.pending.chatId)) {
      const work = this.pending;
      this.pending = { directory: false, chatId: null };
      try {
        await this.run(work);
      } catch (error) {
        if (!this.stopped) this.failed(error, work);
      }
    }
    this.running = false;
    if (!this.stopped) this.idle();
  }
}
